// Fires the [data-ld] reveals. Landing.js calls this once for the whole page;
// a section only writes data-ld="<kind>" (the kinds are in landing-base.css).
//
// hooks/useScrollReveal does not fit: it waits for 15% of the element to be
// visible, while this page fires when the element's TOP crosses 88% of the
// screen, whatever its size, and its class names belong to the old page.
//
//   • data-ld-early fires at 100% of the screen instead of 88%.
//   • group, cards and fields number their children as --ld-i for the stagger.
//   • Fires once and never reverses.
//   • .is-done comes on after the longest reveal has finished, which removes
//     the reveal's rules and hands the element's own transitions back to it.
//   • A mutation observer picks up [data-ld] nodes that mount later, such as
//     the contact form coming back after «إرسال رسالة أخرى».

import { useEffect } from "react";
import { prefersReducedMotion } from "./env";

const STAGGERED = new Set(["group", "cards", "fields"]);
const SETTLE_MS = 4200;

export default function useReveal(rootRef, trigger) {
    useEffect(() => {
        const root = rootRef?.current;
        if (!root) return undefined;

        const timers = new Set();
        const settle = (el) => {
            const t = setTimeout(() => {
                timers.delete(t);
                el.classList.add("is-done");
            }, SETTLE_MS);
            timers.add(t);
        };

        const seen = new WeakSet();
        const show = (el) => {
            el.classList.add("is-in");
            settle(el);
        };

        const still = prefersReducedMotion() || !("IntersectionObserver" in window);
        const onSeen = (entries, io) => {
            entries.forEach((e) => {
                if (!e.isIntersecting) return;
                io.unobserve(e.target);
                show(e.target);
            });
        };
        const late = still ? null : new IntersectionObserver(onSeen, { rootMargin: "0px 0px -12% 0px" });
        const early = still ? null : new IntersectionObserver(onSeen);

        const adopt = () => {
            root.querySelectorAll("[data-ld]").forEach((el) => {
                if (STAGGERED.has(el.dataset.ld)) {
                    Array.from(el.children).forEach((child, i) => child.style.setProperty("--ld-i", String(i)));
                }
                if (seen.has(el)) return;
                seen.add(el);
                if (still) { show(el); return; }
                (el.hasAttribute("data-ld-early") ? early : late).observe(el);
            });
        };

        // Tabbing brings a link just inside the bottom edge, which is below the line
        // where reveals fire, so focus could land on something still invisible.
        // Whatever takes focus shows at once, along with anything hiding it.
        const onFocusIn = (e) => {
            for (let el = e.target.closest?.("[data-ld]"); el; el = el.parentElement?.closest("[data-ld]")) {
                if (el.classList.contains("is-in")) continue;
                late?.unobserve(el);
                early?.unobserve(el);
                show(el);
            }
        };

        adopt();
        const mo = new MutationObserver(adopt);
        mo.observe(root, { childList: true, subtree: true });
        root.addEventListener("focusin", onFocusIn);

        return () => {
            root.removeEventListener("focusin", onFocusIn);
            mo.disconnect();
            late?.disconnect();
            early?.disconnect();
            timers.forEach(clearTimeout);
        };
    }, [rootRef, trigger]);
}
